import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { COMMON } from '@locales/id/messages';
import { Icon } from './Icon';

export interface ComboOption<V> {
  value: V;
  title: string;
  sub?: ReactNode;
}

interface Props<V> {
  /** from `Field`; without one, `label` names the box for screen readers */
  id?: string;
  label?: string;
  describedBy?: string;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** the chosen option's text, shown while the list is closed */
  selected: string;
  query: string;
  onQuery: (q: string) => void;
  options: ComboOption<V>[];
  onPick: (option: ComboOption<V>) => void;
  /** results are being fetched (the list keeps the previous ones meanwhile) */
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** shown when the search found nothing */
  empty: string;
  /** a line under the options ("Ketik untuk mempersempit") */
  footer?: ReactNode;
  /** the list opened or closed (the caller fetches only while it is open) */
  onOpen?: (open: boolean) => void;
}

/**
 * A search box with a list of results under it (combobox, WAI-ARIA 1.2 pattern): type to search, Up/Down to move,
 * Enter to choose, Escape to close. The list sits in the flow (not floating), so it works inside dialogs and on
 * phones; the results come from the caller (usually a server search).
 */
export function Combobox<V>({ id, label, describedBy, invalid, disabled, placeholder, selected, query, onQuery, options, onPick, loading, error, onRetry, empty, footer, onOpen }: Props<V>) {
  const listId = useId();
  const [open, setOpenState] = useState(false);
  const [active, setActive] = useState(0);
  const setOpen = (o: boolean) => {
    setOpenState(o);
    onOpen?.(o);
  };
  const pick = (o: ComboOption<V>) => {
    onPick(o);
    setOpen(false);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return setOpen(true);
      const n = options.length;
      if (n) setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault(); // never submits the form while choosing
      const o = options[Math.min(active, options.length - 1)];
      if (o) pick(o);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation(); // closes the list, not the dialog around it
      setOpen(false);
    }
  };
  const optionId = (i: number) => `${listId}-${i}`;
  const shown = open && !disabled;
  return (
    <div className={`combo ${shown ? 'open' : ''}`}>
      <div className="combo-input">
        <Icon name="search" size={18} />
        <input
          id={id}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={shown}
          aria-controls={listId}
          aria-activedescendant={shown && options.length ? optionId(Math.min(active, options.length - 1)) : undefined}
          aria-label={label}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          placeholder={placeholder}
          value={shown ? query : selected}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            onQuery(e.target.value);
            setActive(0);
            if (!open) setOpen(true);
          }}
          onKeyDown={onKey}
        />
        {loading && shown && <span className="spinner combo-spinner" aria-hidden="true" />}
      </div>
      {shown && (
        <div className="combo-pop">
          <ul id={listId} role="listbox" className="combo-list">
            {options.map((o, i) => (
              <li
                key={String(o.value)}
                id={optionId(i)}
                role="option"
                aria-selected={i === Math.min(active, options.length - 1)}
                className="combo-option"
                // keeps the focus in the input, so the blur does not close the list before the click lands
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}
                onMouseEnter={() => setActive(i)}
              >
                <span className="combo-title">{o.title}</span>
                {o.sub && <span className="combo-sub">{o.sub}</span>}
              </li>
            ))}
          </ul>
          {error ? (
            <div className="combo-state" role="alert">
              <span>{error}</span>
              {onRetry && (
                <button type="button" className="btn btn-sm" onMouseDown={(e) => e.preventDefault()} onClick={onRetry}>
                  {COMMON.retry}
                </button>
              )}
            </div>
          ) : !options.length ? (
            <div className="combo-state" role="status">{loading ? COMMON.loading : empty}</div>
          ) : (
            footer && <div className="combo-state xs">{footer}</div>
          )}
        </div>
      )}
    </div>
  );
}
